import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AnalyticsSummary {
  timeframe: string;
  totalPageviews: number;
  uniqueVisitors: number;
  avgPageviewsPerVisitor: number;
  totalBookings: number;
  topPages: { path: string; views: number; uniqueVisitors: number; percentage: number }[];
  topReferrers: { referrer: string; count: number; percentage: number }[];
  deviceBreakdown: { device: string; count: number; percentage: number }[];
  browserBreakdown: { browser: string; count: number; percentage: number }[];
  osBreakdown: { os: string; count: number; percentage: number }[];
  recentLogs: {
    id: string;
    timestamp: string;
    visitorId: string;
    path: string;
    referrer: string;
    userAgent: string;
    deviceType: string;
    browser: string;
    os: string;
    eventType: string;
    meta?: Record<string, any>;
  }[];
  dailyTrends: { date: string; pageviews: number; visitors: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private lastTrackedPath: string | null = null;
  private isInitialized = false;

  readonly isTrackingEnabled = signal<boolean>(true);

  constructor() {
    this.init();
  }

  /**
   * Initializes router listeners on browser environment
   */
  private init(): void {
    if (this.isInitialized || !isPlatformBrowser(this.platformId)) return;
    this.isInitialized = true;

    // Listen to router navigation events
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects || event.url);
      });
  }

  /**
   * Track client pageview
   */
  public trackPageView(path?: string, title?: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.isTrackingEnabled()) return;

    const currentPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');

    // Prevent duplicate triggers for exact same path in rapid succession
    if (this.lastTrackedPath === currentPath) return;
    this.lastTrackedPath = currentPath;

    const payload = {
      path: currentPath,
      referrer: typeof document !== 'undefined' ? document.referrer : 'Direct',
      title: title || (typeof document !== 'undefined' ? document.title : ''),
      eventType: 'pageview',
      timestamp: new Date().toISOString()
    };

    this.sendCollectBeacon(payload);
  }

  /**
   * Track custom interaction or conversion event
   */
  public trackEvent(
    eventType: 'conversion' | 'interaction' | 'error',
    path: string,
    meta: Record<string, any> = {}
  ): void {
    if (!isPlatformBrowser(this.platformId) || !this.isTrackingEnabled()) return;

    const payload = {
      path,
      referrer: typeof document !== 'undefined' ? document.referrer : 'Direct',
      eventType,
      meta,
      timestamp: new Date().toISOString()
    };

    this.sendCollectBeacon(payload);
  }

  /**
   * Send beacon payload to internal analytics collection endpoint
   */
  private sendCollectBeacon(payload: Record<string, any>): void {
    const url = `${environment.apiBaseUrl}/api/analytics/collect`;

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      } catch (err) {
        // Fallback to HTTP POST if beacon fails
      }
    }

    this.http.post(url, payload).subscribe({
      error: (err) => console.warn('[AnalyticsService] Analytics collection warning:', err)
    });
  }

  /**
   * Fetch internal analytics summary statistics
   */
  public getSummary(timeframe: '24h' | '7d' | '30d' | 'all' = '7d'): Observable<AnalyticsSummary> {
    return this.http.get<AnalyticsSummary>(
      `${environment.apiBaseUrl}/api/analytics/summary?timeframe=${timeframe}`
    );
  }

  /**
   * Fetch recent analytics raw activity logs
   */
  public getLogs(timeframe: '24h' | '7d' | '30d' | 'all' = 'all'): Observable<{ logs: any[]; total: number }> {
    return this.http.get<{ logs: any[]; total: number }>(
      `${environment.apiBaseUrl}/api/analytics/logs?timeframe=${timeframe}`
    );
  }

  /**
   * Clear all analytics data
   */
  public clearAnalytics(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${environment.apiBaseUrl}/api/analytics/clear`,
      {}
    );
  }
}
